/** The view states (ruling 0.5): eight distinct conditions plus `ready`, rendered by one banner. */
import { ContractViolation } from "../contracts/decode.ts";
import type { ReadEnvelope } from "../contracts/envelope.ts";
import { ApiRequestError } from "./client.ts";
import { formatDurationUs } from "./format.ts";
import { h } from "./dom.ts";

export type ViewState =
  | { readonly kind: "loading" }
  | { readonly kind: "no-observations" }
  | { readonly kind: "no-qualifying"; readonly reason: string }
  | { readonly kind: "unavailable"; readonly reason: string }
  | { readonly kind: "stale"; readonly ageUs: number | null; readonly superseded: boolean }
  | { readonly kind: "disconnected"; readonly reason: string }
  | { readonly kind: "unauthorised"; readonly status: number }
  | { readonly kind: "error"; readonly correlationId: string | null; readonly message: string }
  | { readonly kind: "ready" };

/** Derive the state of a read: unavailable beats stale beats empty beats ready. */
export function stateOfEnvelope(envelope: ReadEnvelope<unknown>): ViewState {
  if (envelope.freshness === "UNAVAILABLE") {
    return { kind: "unavailable", reason: "the projection cannot state its lag" };
  }
  if (envelope.freshness === "STALE" || envelope.superseded) {
    return { kind: "stale", ageUs: envelope.ageUs, superseded: envelope.superseded };
  }
  return envelope.page.rows.length === 0 ? { kind: "no-observations" } : { kind: "ready" };
}

/** Map a failure to a state: a refusal by status, a bad shape as an error, a dead network as disconnected. */
export function stateOfError(error: unknown): ViewState {
  if (error instanceof ApiRequestError) {
    const correlationId = error.body?.correlationId ?? null;
    if (error.status === 401 || error.status === 403) {
      return { kind: "unauthorised", status: error.status };
    }
    if (error.status === 404) {
      return { kind: "no-observations" };
    }
    if (error.status === 503 || error.status === 429) {
      return { kind: "unavailable", reason: `the service answered ${String(error.status)}` };
    }
    return {
      kind: "error",
      correlationId,
      message: `the request failed (${String(error.status)})`,
    };
  }
  if (error instanceof ContractViolation) {
    return {
      kind: "error",
      correlationId: null,
      message: "the server's answer had an unexpected shape",
    };
  }
  return { kind: "disconnected", reason: "the server could not be reached" };
}

interface Presentation {
  readonly symbol: string;
  readonly text: string;
  readonly alert: boolean;
}

function present(state: ViewState): Presentation {
  switch (state.kind) {
    case "loading":
      return { symbol: "…", text: "Loading.", alert: false };
    case "no-observations":
      return { symbol: "○", text: "No observations yet for this view.", alert: false };
    case "no-qualifying":
      return { symbol: "○", text: `No qualifying opportunities: ${state.reason}.`, alert: false };
    case "unavailable":
      return {
        symbol: "!",
        text: `Data unavailable: ${state.reason}. This is not an empty, healthy market.`,
        alert: true,
      };
    case "stale": {
      const age = state.ageUs === null ? "" : ` (age ${formatDurationUs(state.ageUs)})`;
      const why = state.superseded ? "A newer view has replaced this one" : "Stale data";
      return { symbol: "◷", text: `${why}${age}. Actions are disabled.`, alert: true };
    }
    case "disconnected":
      return {
        symbol: "×",
        text: `Disconnected: ${state.reason}. Commands are disabled until a consistent snapshot is reconciled.`,
        alert: true,
      };
    case "unauthorised":
      return {
        symbol: "⊘",
        text: `Not authorised (status ${String(state.status)}). Sign in again or ask for access.`,
        alert: true,
      };
    case "error": {
      const ref = state.correlationId === null ? "" : ` Reference ${state.correlationId}.`;
      return { symbol: "!", text: `${state.message}.${ref}`, alert: true };
    }
    case "ready":
      return { symbol: "✓", text: "Up to date.", alert: false };
  }
}

/** The one banner every view uses; text and a symbol accompany the colour, and it is a live region. */
export function renderStateBanner(state: ViewState): HTMLElement {
  const shown = present(state);
  return h(
    "div",
    {
      class: `state-banner state-${state.kind}`,
      attrs: {
        role: shown.alert ? "alert" : "status",
        "aria-live": shown.alert ? "assertive" : "polite",
        "data-state": state.kind,
      },
    },
    h("span", { class: "state-symbol", attrs: { "aria-hidden": "true" } }, shown.symbol),
    h("span", { class: "state-text" }, shown.text),
  );
}
